import React, { useState } from 'react';
import './Evaluation.css';
import { IconWeb, IconSearch, IconMegaphone, IconVideo, IconChart } from './Icons';

const Evaluation = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const questions = [
    {
      category: 'Business & Digital Foundation',
      stepNum: '01',
      questions: [
        {
          id: 'business_type',
          question: 'What is your core business operational model?',
          type: 'select',
          options: ['B2B Enterprise & Corporate', 'E-commerce & Retail Store', 'Healthcare & Clinical Services', 'Industrial & Manufacturing', 'SaaS / Technology Product', 'Local Service Business']
        },
        {
          id: 'business_stage',
          question: 'What stage of digital growth are you currently in?',
          type: 'select',
          options: ['Startup / Modernizing Foundation', 'Growing Firm (1-3 years)', 'Established Enterprise (3+ years)', 'Rapidly Scaling Market Leader']
        },
        {
          id: 'main_goal',
          question: 'What is your primary technological or revenue objective?',
          type: 'select',
          options: ['Scale Lead Generation & Conversions', 'Deploy Custom Web & Mobile Platforms', 'Maximize SEO & Organic Search Dominance', 'Modernize Brand Identity & High-End Media', 'Enterprise Cloud & DevOps Migration']
        }
      ]
    },
    {
      category: 'Web Platform & Technical Architecture',
      stepNum: '02',
      questions: [
        {
          id: 'website_exists',
          question: 'What is the current state of your web or digital infrastructure?',
          type: 'select',
          options: ['Modern, fully-functional custom platform', 'Legacy platform requiring architectural upgrade', 'Planning a complete enterprise redesign', 'Need a new digital ecosystem from scratch']
        },
        {
          id: 'seo_knowledge',
          question: 'How does your website currently perform in Google Search (SEO)?',
          type: 'select',
          options: ['Top rankings for high-intent keywords', 'Moderate rankings, but losing traffic to rivals', 'Limited search visibility on page 2+', 'Zero active rankings or technical SEO strategy', 'Not currently tracked']
        },
        {
          id: 'organic_traffic',
          question: 'What percentage of your qualified leads originate from Organic Search?',
          type: 'select',
          options: ['More than 50% of revenue pipeline', '25% - 50% steady organic flow', 'Less than 25% (heavily reliant on referrals)', 'Unsure / No analytics dashboard']
        }
      ]
    },
    {
      category: 'Performance Advertising & Paid Acquisition',
      stepNum: '03',
      questions: [
        {
          id: 'ads_running',
          question: 'Are you currently running active paid acquisition campaigns?',
          type: 'select',
          options: ['Yes, Meta Ads (Facebook/Instagram)', 'Yes, Google Search & Display Ads', 'Omnichannel (Meta, Google & LinkedIn)', 'Not currently, but actively seeking high ROAS', 'No paid campaigns planned']
        },
        {
          id: 'ads_budget',
          question: 'What is your planned monthly digital growth budget?',
          type: 'select',
          options: ['Under ₹25,000 / month', '₹25,000 - ₹75,000 / month', '₹75,000 - ₹2,00,000 / month', 'Enterprise Scale (₹2,00,000+ / month)']
        },
        {
          id: 'ads_results',
          question: 'How would you rate your current paid ad conversion efficiency (ROAS)?',
          type: 'select',
          options: ['Highly Profitable (4x+ ROAS)', 'Acceptable, but high cost per acquisition', 'Breaking even / in need of optimization', 'Not running paid media currently']
        }
      ]
    },
    {
      category: 'Brand Media & Visual Storytelling',
      stepNum: '04',
      questions: [
        {
          id: 'content_strategy',
          question: 'Do you have high-resolution photography and cinematic brand video assets?',
          type: 'select',
          options: ['Yes, premium studio-grade media library', 'Some basic assets, but need corporate upgrade', 'Lacking professional photo/video production', 'In urgent need of commercial brand production']
        },
        {
          id: 'social_media',
          question: 'Which enterprise social & lead channels are most critical for you?',
          type: 'multiselect',
          options: ['LinkedIn Enterprise', 'Instagram & Meta', 'Google Business Profile', 'YouTube Commercials', 'Direct WhatsApp Inquiries']
        }
      ]
    },
    {
      category: 'Analytics & Conversion Intelligence',
      stepNum: '05',
      questions: [
        {
          id: 'analytics_setup',
          question: 'Do you utilize automated analytics and conversion tracking?',
          type: 'select',
          options: ['Full Google Analytics 4 & Custom Dashboards', 'Partial tracking set up', 'No real-time tracking implemented', 'Seeking automated KPI reporting']
        },
        {
          id: 'conversion_tracking',
          question: 'How effectively do you track cost-per-lead and visitor conversions?',
          type: 'select',
          options: ['Comprehensive CRM and event tracking', 'Basic lead form notifications only', 'Manual spreadsheet tracking', 'Need end-to-end CRM integration']
        }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateResults = () => {
    let score = 50;

    if (answers.website_exists === 'Modern, fully-functional custom platform') score += 15;
    if (answers.website_exists === 'Legacy platform requiring architectural upgrade') score += 5;
    if (answers.seo_knowledge === 'Top rankings for high-intent keywords') score += 15;
    if (answers.seo_knowledge === 'Moderate rankings, but losing traffic to rivals') score += 8;
    if (answers.ads_running?.includes('Omnichannel') || answers.ads_running?.includes('Meta') || answers.ads_running?.includes('Google')) score += 10;
    if (answers.content_strategy === 'Yes, premium studio-grade media library') score += 10;
    if (answers.analytics_setup?.includes('Full')) score += 10;

    const calculatedScore = Math.min(Math.max(score, 25), 98);

    const recommendations = [];

    if (answers.website_exists !== 'Modern, fully-functional custom platform') {
      recommendations.push({
        service: 'Custom Web & Cloud Engineering',
        priority: 'High',
        description: 'Deploy a high-performance, responsive web platform engineered for sub-second speeds and maximum conversion integrity.',
        icon: <IconWeb size={24} />
      });
    }

    if (answers.seo_knowledge !== 'Top rankings for high-intent keywords') {
      recommendations.push({
        service: 'Technical SEO & Search Dominance',
        priority: 'High',
        description: 'Implement comprehensive technical SEO architecture, schema markup, and keyword domination to capture high-intent organic buyers.',
        icon: <IconSearch size={24} />
      });
    }

    if (answers.ads_results !== 'Highly Profitable (4x+ ROAS)') {
      recommendations.push({
        service: 'Performance Ads & Meta Marketing',
        priority: 'Medium',
        description: 'Launch targeted Meta and Google ad funnels with precise audience retargeting to maximize customer acquisition and ROAS.',
        icon: <IconMegaphone size={24} />
      });
    }

    if (answers.content_strategy !== 'Yes, premium studio-grade media library') {
      recommendations.push({
        service: 'Commercial Photo & Video Production',
        priority: 'Medium',
        description: 'Upgrade your visual brand presence with 4K cinematic corporate films, executive portraits, and product showcases.',
        icon: <IconVideo size={24} />
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        service: 'Enterprise Technology Scale Roadmap',
        priority: 'High',
        description: 'Comprehensive quarterly advisory to scale cloud infrastructure, automated pipelines, and international market expansion.',
        icon: <IconChart size={24} />
      });
    }

    setResults({
      recommendations,
      score: calculatedScore
    });
    setCurrentStep(questions.length);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResults(null);
  };

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const currentCategory = questions[currentStep];

  return (
    <section id="evaluation" className="evaluation">
      <div className="evaluation-container">
        <div className="section-header">
          <div className="section-badge">
            <span>Free 2-Min Assessment</span>
          </div>
          <h2 className="section-title">Check Your Digital Readiness & Growth Score</h2>
          <p className="section-subtitle">
            Answer a few quick questions about your web presence, tech stack, and marketing. Receive an instant assessment score and practical recommendations for your business.
          </p>
        </div>

        {/* Diagnostic Form */}
        {!results && currentCategory && (
          <div className="evaluation-card">
            <div className="eval-card-header">
              <div className="eval-step-indicator">
                <span className="step-pill">Stage {currentStep + 1} of {questions.length}</span>
                <span className="step-category-name">{currentCategory.category}</span>
              </div>
              <div className="eval-progress-bar">
                <div
                  className="eval-progress-fill"
                  style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="eval-questions-body">
              {currentCategory.questions.map((q) => (
                <div key={q.id} className="eval-question-group">
                  <label className="eval-question-label">{q.question}</label>
                  {q.type === 'select' ? (
                    <select
                      value={answers[q.id] || ''}
                      onChange={(e) => handleAnswer(q.id, e.target.value)}
                      className="eval-select-input"
                    >
                      <option value="">Select assessment option...</option>
                      {q.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : q.type === 'multiselect' ? (
                    <div className="eval-multiselect-grid">
                      {q.options.map((opt) => {
                        const selected = Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                              const updated = selected
                                ? current.filter(o => o !== opt)
                                : [...current, opt];
                              handleAnswer(q.id, updated);
                            }}
                            className={`eval-pill-option ${selected ? 'selected' : ''}`}
                          >
                            <span>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="eval-actions-footer">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="btn btn-secondary eval-nav-btn"
              >
                Previous Step
              </button>
              <button
                onClick={handleNext}
                className="btn btn-primary eval-nav-btn"
              >
                {currentStep === questions.length - 1 ? 'Generate Audit Report ➔' : 'Continue ➔'}
              </button>
            </div>
          </div>
        )}

        {/* Results Dashboard */}
        {results && (
          <div className="eval-results-dashboard">
            <div className="eval-score-card">
              <div className="score-badge">Assessment Result</div>
              <h3 className="score-headline">Digital Maturity Index</h3>
              
              <div className="score-meter-circle">
                <span className="score-meter-num">{results.score}%</span>
                <span className="score-meter-label">Overall Readiness</span>
              </div>

              <p className="score-assessment-text">
                {results.score >= 80 ? 'Outstanding. Your digital architecture is mature. We recommend specialized microservices and international growth pipelines.' :
                 results.score >= 60 ? 'Good Foundation. Significant high-ROI opportunities exist in automated technical SEO, UI/UX refinement, and performance media.' :
                 'High Growth Potential. Upgrading your custom web platform and technical SEO will dramatically accelerate customer acquisition.'}
              </p>
            </div>

            <div className="eval-recommendations-card">
              <h3 className="rec-section-title">Prioritized Strategic Recommendations</h3>
              <div className="rec-cards-grid">
                {results.recommendations.map((rec, idx) => (
                  <div key={idx} className="rec-item-card">
                    <div className="rec-item-top">
                      <div className="rec-item-icon-box">{rec.icon}</div>
                      <span className={`rec-priority-badge ${rec.priority.toLowerCase()}`}>
                        {rec.priority} Priority
                      </span>
                    </div>
                    <h4 className="rec-item-title">{rec.service}</h4>
                    <p className="rec-item-desc">{rec.description}</p>
                  </div>
                ))}
              </div>

              <div className="eval-cta-banner">
                <div className="cta-banner-text">
                  <h4>Ready to Execute Your Growth Roadmap?</h4>
                  <p>Schedule a strategic consultation with Trevox technology directors to discuss your custom execution plan.</p>
                </div>
                <div className="cta-banner-buttons">
                  <button className="btn btn-primary" onClick={() => scrollTo('contact')}>
                    Book Consultation
                  </button>
                  <button className="btn btn-secondary" onClick={handleRestart}>
                    Retake Assessment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Evaluation;
